import React, { Fragment, FC, useRef, useEffect, useMemo, useState, Suspense } from 'react';
import { CrewTimesheetSummary } from './crew-timesheet-summary';
import { DateHeader, CrewHeader } from '~/components';
import { TimesheetCostCodeActions } from './cost-code/actions';
import { CostCodes } from './cost-code';
import { useParams, useLocation } from 'react-router-dom';
import { useAppUser, ErrorBoundary, SuspenseLoader } from '@bechtel/client-common';
import { NetworkStatus, ApolloError } from '@apollo/client';
import { get, forEach, findIndex, concat, find, filter, sortBy, flatMap } from 'lodash';
import { Box, Spinner, SpinnerSize, Text, Button, Icon, useToastsDispatch, ToastAction, ToastType } from '@bechtel/gds';
import { Trans, t } from '@lingui/macro';
import { formatISO } from 'date-fns';
import { DetailDateHeaderStyled } from './styled';
import {
  CostCode,
  CrewMember,
  TimeEntryInput,
  PersonaType,
  TimesheetDayError,
  Crew,
  SaveTimesheetInput,
  SaveTimesheetChargesError,
} from '@bechtel/types';
import { TimesheetDetailProps, TSCostCode, TimesheetByHourCode, TimesheetFormikState, TimesheetReasons } from './types';
import { useTimesheetByForeman, useSaveTimeSheet, usePinnedCostCodesByCrew } from '~/hooks';
import { Form, Formik, FormikProps, FieldArrayRenderProps, FormikHelpers as FormikActions } from 'formik';
import { getTimesheetData, getTimesheetParsedValues, getChangedTimeInputs } from '~/detail/utils';
import { TimesheetContext } from '~/detail/context';
import { array, object, string, number } from 'yup';
import { SaveTimeSheetError } from '~/components/organisms';
//@ts-ignore needed for declaration generation
import { ReactComponent as PackageIcon } from '@bechtel/client-assets/dist/images/crew-change.svg';

interface RouteParams {
  id: string;
}

const UpdatedTimeSheetToast = () => {
  return (
    <Box p={{ sm: '500' }} flexDirection={{ sm: 'column' }}>
      <Text family="graphik" weight="600" size="500">
        <Trans>Timesheet updated successfully.</Trans>
      </Text>
    </Box>
  );
};

export const TimesheetDetail: FC<TimesheetDetailProps> = ({ showDateHeader, forDate, setForDate }) => {
  const dispatchToast = useToastsDispatch();
  const [actionCompleted, setActionCompleted] = useState<boolean>(false);
  const { user } = useAppUser();
  const { id } = useParams();
  const { state } = useLocation();
  const foremanId =
    user?.crewMember.persona === PersonaType.FOREMAN ? user?.crewMember.id : id ? decodeURIComponent(id) : undefined;
  const userCrew: Crew = state ? state : user?.crewMember?.crew;
  const crewTimesheetRef = useRef<HTMLDivElement>(null);
  const costCodeListRef = useRef<HTMLDivElement[]>([]);
  const [saveTimeSheetError, setSaveTimeSheetError] = useState<SaveTimesheetChargesError[] | ApolloError | null>(null);
  const [collapseAll, setCollapseAll] = useState(false);
  const [collapseList, setCollapseList] = useState<string[]>([]);
  const setShowAddCommentSidesheet = useState(false)[1];
  const setViewCommentsSidesheet = useState(false)[1];
  const [costCodes, setCostCodes] = useState<CostCode[]>([]);
  const formattedDate = formatISO(forDate, { representation: 'date' });

  const closeAddCommentSidesheet = () => {
    setShowAddCommentSidesheet(false);
  };

  const closeViewCommentsSidesheet = () => {
    setViewCommentsSidesheet(false);
  };

  const onCrewTimesheetScroll = (e) => {
    if (!costCodeListRef || costCodeListRef.current.length == 0) return;
    e.stopPropagation();
    forEach(costCodeListRef.current, (costCodeItemRef) => {
      if (costCodeItemRef) costCodeItemRef.scrollLeft = e.target.scrollLeft;
    });
  };
  const onCostCodeListScroll = (e) => {
    if (!crewTimesheetRef || !crewTimesheetRef.current) return;
    e.stopPropagation();
    crewTimesheetRef.current.scrollLeft = e.target.scrollLeft;
    forEach(costCodeListRef.current, (costCodeItemRef) => {
      if (costCodeItemRef) costCodeItemRef.scrollLeft = e.target.scrollLeft;
    });
  };

  const closeModal = () => {
    setSaveTimeSheetError(null);
  };

  const [
    loadPinnedCostCodes,
    {
      loading: pinnedCostCodesLoading,
      data: pinnedCostCodesData,
      error: pinnedCostCodesError,
      refetch: pinnedCostCodesRefetch,
      networkStatus: pinnedCostCodesNetworkStatus,
    },
  ] = usePinnedCostCodesByCrew(true);

  const [loadTimesheetByForeman, { loading, data, error, refetch, networkStatus }] = useTimesheetByForeman(true);
  const timesheetParsedValues = getTimesheetParsedValues(data);
  const {
    timesheetDay,
    timesheetGetError,
    crewEmployees,
    foreman,
    generalForeman,
    timesheetDayCrewMembers,
    shift,
    crew,
    timesheetId,
    crewId,
    duration,
    roundingIncrement,
  } = timesheetParsedValues;
  const timesheetError = error || timesheetGetError;
  const pinnedCostCodes = get(pinnedCostCodesData, 'pinnedCostCodesByCrew.timesheetCostCodes', []);
  const costCodeFieldArrayHelperRef = useRef<FieldArrayRenderProps>();

  const SaveTimesheetValidationSchema = object().shape({
    timesheetCostCodes: array().of(
      object().shape({
        hourCodeTimeEntry: array().of(
          object().shape({
            timeEntries: array().of(
              object().shape({
                hourCode: string().required(),
                hours: number()
                  .min(0, t`Hours should not be in minus`)
                  .test({
                    name: 'max hours',
                    test: (val, context) => {
                      const hourType = find(
                        shift?.dayShiftSchedule?.hourTypes,
                        (hourType) => hourType.hourCode === context.parent.hourCode
                      );
                      if (hourType) {
                        const maxValue = hourType.maxHours == 0 ? (duration == 0 ? 24 : duration) : hourType.maxHours;
                        if (val && val > maxValue) {
                          return context.createError({
                            message: t`Hours should not be greater than ${maxValue}.`,
                          });
                        }
                      }
                      return true;
                    },
                  })
                  .typeError(t`Hours must be a number`)
                  .nullable(),
                otherCommentDescription: string()
                  .nullable()
                  .notRequired()
                  .when('commentId', {
                    is: (commentId) => commentId === TimesheetReasons.TIMESHEET_REASON_OTHER,
                    then: string()
                      .required(t`Other comment is mandatory`)
                      .nullable(),
                  }),
              })
            ),
          })
        ),
      })
    ),
  });

  //Sorted Timesheet employee list with foreman & general foreman in the start
  const timesheetCrewEmployees = useMemo(() => {
    if (!crewEmployees || !foreman || !generalForeman) return [];
    let sortedCrewEmployees: CrewMember[] = [];
    const foremanPos = findIndex(crewEmployees, (crewEmployee) => crewEmployee.id === foreman.id);
    const generalForemanPos = generalForeman
      ? findIndex(crewEmployees, (crewEmployee) => crewEmployee.id === generalForeman.id)
      : null;
    //Foreman should be first in the list
    if (foremanPos != -1) sortedCrewEmployees = sortedCrewEmployees.concat(crewEmployees[foremanPos]);
    //General Foreman should be second in the list
    if (generalForemanPos && generalForemanPos != -1)
      sortedCrewEmployees = sortedCrewEmployees.concat(crewEmployees[generalForemanPos]);
    //All the other crew employees sorted by first name
    const craftEmployees = sortBy(
      filter(
        crewEmployees,
        (crewEmployee) => crewEmployee.id !== foreman?.id && crewEmployee.id !== generalForeman?.id
      ),
      (crewEmployee) => crewEmployee.firstName
    );
    if (craftEmployees && craftEmployees.length > 0) sortedCrewEmployees = sortedCrewEmployees.concat(craftEmployees);
    return sortedCrewEmployees;
    //All the other crew employees sorted by first name
  }, [foreman, generalForeman, crewEmployees]);

  const timesheetCostCodesInitalData: TimesheetFormikState | undefined = useMemo(() => {
    if (pinnedCostCodesLoading || loading) return;
    return getTimesheetData(
      pinnedCostCodes,
      timesheetCrewEmployees,
      shift?.dayShiftSchedule?.hourTypes,
      timesheetDayCrewMembers,
      costCodes //No neeed for CostCodes as dependency as this mainly dependent on timesheetDayCrewMembers changes
    );
  }, [
    pinnedCostCodes,
    timesheetCrewEmployees,
    shift?.dayShiftSchedule?.hourTypes,
    timesheetDayCrewMembers,
    getTimesheetData,
    pinnedCostCodesLoading,
    loading,
  ]);

  //Load Pinned Costcodes & Timesheet for thaat day
  useEffect(() => {
    if (!userCrew || !userCrew.id) return;
    loadPinnedCostCodes({
      variables: {
        input: {
          crewId: userCrew.id,
        },
      },
    });
  }, [userCrew, userCrew?.id]);

  useEffect(() => {
    if (!foremanId) return;
    loadTimesheetByForeman({
      variables: {
        input: {
          foremanId: foremanId,
          forDate: formattedDate,
        },
        forDate: formattedDate,
        crewNumber: userCrew && userCrew.crewNumber ? userCrew.crewNumber : '',
      },
    });
  }, [foremanId, formattedDate, userCrew]);

  useEffect(() => {
    if (
      collapseList.length > 0 &&
      collapseList.length === costCodeFieldArrayHelperRef.current?.form.values?.timesheetCostCodes?.length
    ) {
      setCollapseAll(true);
    }
    if (collapseList.length === 0) {
      setCollapseAll(false);
    }
  }, [collapseList.length, costCodeFieldArrayHelperRef.current?.form.values?.timesheetCostCodes?.length]);

  const handleCostCodeAdded = (selectedCostCodes: CostCode[]) => {
    //TODO Existing timsheet costcodes filter out
    if (
      !selectedCostCodes ||
      !selectedCostCodes.length ||
      !costCodeFieldArrayHelperRef ||
      !costCodeFieldArrayHelperRef.current
    )
      return;
    forEach(selectedCostCodes, (costCode) => {
      let timesheetByHourCodes: TimesheetByHourCode[] = [];
      forEach(shift?.dayShiftSchedule?.hourTypes, (hourType) => {
        let hourCodeTimeEntries: TimeEntryInput[] = [];
        forEach(timesheetCrewEmployees, (crewEmployee, index) => {
          const timeentry: TimeEntryInput = {
            hourCode: hourType.hourCode,
            hours: '',
            costCodeId: costCode.id,
            crewMemberId: crewEmployee.id,
          };
          hourCodeTimeEntries = concat(hourCodeTimeEntries, timeentry);
        });
        const timesheetByHourCode: TimesheetByHourCode = {
          hourCode: hourType.hourCode,
          timeEntries: hourCodeTimeEntries,
          allowEdit: true,
        };
        timesheetByHourCodes = concat(timesheetByHourCodes, timesheetByHourCode);
      });
      costCodeFieldArrayHelperRef?.current?.push({
        costCode: costCode,
        hourCodeTimeEntry: timesheetByHourCodes,
      });
    });
    setCostCodes([...costCodes, ...selectedCostCodes]);
  };

  const [saveTimeSheet, { loading: savingTimesheet, error: mutationError, data: saveTimesheetData }] = useSaveTimeSheet(
    () => {
      setActionCompleted(true);
    }
  );

  useEffect(() => {
    if (!actionCompleted) return;
    const error = get(saveTimesheetData, 'saveTimesheet.errors', '') || mutationError;
    if (!error) {
      const toastAction: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.INFO,
          content: UpdatedTimeSheetToast,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(toastAction);
    } else {
      setSaveTimeSheetError(error);
    }
    setActionCompleted(false);
  }, [actionCompleted, dispatchToast, saveTimesheetData, mutationError]);

  useEffect(() => {
    setCostCodes(
      timesheetCostCodesInitalData?.timesheetCostCodes
        ? flatMap(timesheetCostCodesInitalData?.timesheetCostCodes, 'costCode')
        : []
    );
  }, [timesheetCostCodesInitalData?.timesheetCostCodes]);

  if (!foremanId)
    return (
      <Box
        p={{ sm: '500' }}
        alignItems={{ sm: 'center' }}
        justifyContent={{ sm: 'center' }}
        flexGrow={{ sm: 1 }}
        css={'height: 100%;background-color:white'}
        flexDirection={{ sm: 'column' }}
      >
        <PackageIcon />
        <Box mt={{ sm: '700' }} px={{ sm: '600' }}>
          <Text family="graphik" color="grey.600">
            <Trans>Please select a crew to view timesheet</Trans>
          </Text>
        </Box>
      </Box>
    );

  return (
    <Fragment>
      {showDateHeader && (
        <DetailDateHeaderStyled data-testid="header-container">
          <DateHeader selectedDate={forDate} onDateSelect={(date) => setForDate(date)} disable={loading} />
        </DetailDateHeaderStyled>
      )}
      {loading && networkStatus !== NetworkStatus.refetch && (
        <Box
          p={{ sm: '500' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          css={'height: 100%;'}
          data-testid="timesheet-loader"
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!loading && timesheetError && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
          style={{ width: '100%', height: '100%' }}
          data-testid="timesheet-error-container"
        >
          <Box pb={{ sm: '500' }}>
            <Text>{getErrorMessage(timesheetError)}</Text>
          </Box>
          <Button onClick={() => refetch()} style={{ maxWidth: '120px' }}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      )}
      {!loading && !timesheetCostCodesInitalData && <Text>No Data.</Text>}
      {(!loading || networkStatus === NetworkStatus.refetch) &&
        !timesheetError &&
        (!shift ||
          !shift.dayShiftSchedule ||
          !shift.dayShiftSchedule.hourTypes ||
          shift?.dayShiftSchedule?.hourTypes.length == 0) && (
          <Box
            p={{ sm: '500' }}
            alignItems={{ sm: 'center' }}
            justifyContent={{ sm: 'center' }}
            flexGrow={{ sm: 1 }}
            css={'height: 100%;'}
          >
            <Text>
              <Trans>No hour types defined for shift.</Trans>
            </Text>
          </Box>
        )}
      {(!loading || networkStatus === NetworkStatus.refetch) && !timesheetError && timesheetDay?.id && (
        <>
          <TimesheetContext
            forDate={forDate}
            crewEmployees={timesheetCrewEmployees}
            hourTypes={shift?.dayShiftSchedule?.hourTypes}
            duration={duration}
            isEditable={timesheetDay?.editable && !savingTimesheet}
            roundingIncrement={roundingIncrement}
          >
            <Formik
              initialValues={timesheetCostCodesInitalData}
              validationSchema={SaveTimesheetValidationSchema}
              onSubmit={async (values: TimesheetFormikState, props: FormikActions<TimesheetFormikState>) => {
                const { setSubmitting } = props;
                setSubmitting(true);
                const timeEntryInputs: TimeEntryInput[] = getChangedTimeInputs(timesheetCostCodesInitalData, values);

                const saveTimesheetInput: SaveTimesheetInput = {
                  timesheetId: timesheetId,
                  crewId: crewId,
                  forDate: formatISO(forDate, { representation: 'date' }),
                  timeEntryInput: timeEntryInputs,
                };

                await saveTimeSheet({
                  variables: {
                    input: saveTimesheetInput,
                  },
                });
                setSubmitting(false);
              }}
              enableReinitialize={true}
              validateOnBlur={true}
              validateOnChange={false}
            >
              {(props: FormikProps<TimesheetFormikState>) => (
                <Form>
                  <CrewHeader
                    crew={crew}
                    timesheetStatus={timesheetDay.timesheetStatus}
                    timesheetParsedValues={timesheetParsedValues}
                    savingTimesheet={savingTimesheet}
                  />
                  <CrewTimesheetSummary
                    crew={crew}
                    crewTimesheetRef={crewTimesheetRef}
                    onCrewTimesheetScroll={(e) => {
                      onCrewTimesheetScroll(e);
                    }}
                  />
                  <TimesheetCostCodeActions
                    foreman={foreman}
                    crewId={crew?.id}
                    onCostCodeAdded={handleCostCodeAdded}
                    collapseAll={collapseAll}
                    tsCostCodes={costCodes}
                    setCollapseAll={() => {
                      if (!collapseAll) {
                        setCollapseList(
                          flatMap(
                            costCodeFieldArrayHelperRef.current?.form.values?.timesheetCostCodes,
                            (item: TSCostCode) => {
                              return item.costCode.id;
                            }
                          )
                        );
                      } else {
                        setCollapseList([]);
                      }
                      setCollapseAll(!collapseAll);
                    }}
                  />
                  <CostCodes
                    costCodeFieldArrayHelperRef={costCodeFieldArrayHelperRef}
                    collapseAll={collapseAll}
                    collapseList={collapseList}
                    setCollapseList={(list) => setCollapseList(list)}
                    crewId={crewId}
                    costCodeListRef={costCodeListRef}
                    onCostCodeListScroll={onCostCodeListScroll}
                    showDateHeader={showDateHeader}
                  />
                </Form>
              )}
            </Formik>
          </TimesheetContext>
        </>
      )}
      {saveTimeSheetError && (
        <ErrorBoundary>
          <Suspense fallback={<SuspenseLoader></SuspenseLoader>}>
            <SaveTimeSheetError
              errors={saveTimeSheetError}
              crewMemberList={timesheetCrewEmployees}
              close={closeModal}
            />
          </Suspense>
        </ErrorBoundary>
      )}
    </Fragment>
  );
};

const getErrorMessage = (error: TimesheetDayError | ApolloError) => {
  if (error instanceof ApolloError)
    return <Trans>Network error has occured. Please click the button below to try again.</Trans>;
  switch (error) {
    case TimesheetDayError.CREW_MEMBER_NOT_FOUND:
      return <Trans>Crew member not found. Please click the button below to try again.</Trans>;
    case TimesheetDayError.CREW_NOT_FOUND:
      return <Trans>Crew not found. Please click the button below to try again.</Trans>;
    case TimesheetDayError.TIMESHEET_DAY_ERRORED:
      return <Trans>Some error has occured. Please click the button below to try again.</Trans>;
    default:
      return <Trans>There was a problem loading Timesheet Details. Please click the button below to try again.</Trans>;
  }
};
